import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import postal from 'postal';
import ErrorBoundary from '../../../shared/error-boundary';
import Loading from '../../../shared/loading';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import mutate from '../../../shared/mutate';
import {
  SortableFilterableTableHeader,
  SORT_DIRECTION,
  filterSimpleColumns,
  sortSimpleColumn,
} from '../../../shared/sortable-filterable-table-header';
import EditPartnershipModal from '../partnerships/edit-partnership-modal';
import ViewContactModal from './view-contact-modal';
import { getById, toggleArchivingStatus } from '../../../services/lwadmin/partnership/partnership-service';

function filterContacts(contacts, state) {
  let filteredContacts = _(contacts);

  if (state.wireIdFilter != null) {
    filteredContacts = filteredContacts.filter(x => x.referable_type === 'Wire' && state.wireIdFilter === x.referable_id);
  }

  if (state.filterOutArchivedContact) {
    filteredContacts = filteredContacts.filter({ archived: false });
  }

  filteredContacts = filterSimpleColumns(filteredContacts, state, 'name', 'email', 'phone', 'investor_type');

  return filteredContacts.value();
}

function sortContacts(contacts, state) {
  let sortedContacts = _(contacts);

  sortedContacts = sortSimpleColumn(sortedContacts, 'created_at', state);
  sortedContacts = sortSimpleColumn(sortedContacts, 'name', state);
  sortedContacts = sortSimpleColumn(sortedContacts, 'email', state);
  sortedContacts = sortSimpleColumn(sortedContacts, 'phone', state);
  sortedContacts = sortSimpleColumn(sortedContacts, 'investor_type', state);
  sortedContacts = sortSimpleColumn(sortedContacts, 'sent_at', state);

  return sortedContacts.value();
}

function filterAndSortContacts(contacts, state) {
  return sortContacts(filterContacts(contacts, state), state);
}

const Contact = source => ({
  ...(source || {
    id: null,
    profile_id: null,
    name: '',
    email: '',
    phone: '',
    investor_type: '',
    referable_type: null,
    referable_id: null,
  }),
  created_at: _.get(source, 'created_at') ? moment(source.created_at) : null,
  sent_at: _.get(source, 'sent_at') ? moment(source.sent_at) : null,
});

const Partnership = source => ({
  ...(source || {
    id: null,
    name: '',
    active: false,
    send_automatically: false,
    contact_email: '',
    partnership_type: '',
    dashboard_url: '',
  }),
  contacts: _.get(source, 'contacts', []).map(Contact),
});

/* Props:
- partnershipId
- wireId: the id of the wire to limit contacts to. if not provided, all wire contacts are displayed.
*/
export default class PartnershipAdmin extends React.Component {
  static propTypes = {
    partnershipId: PropTypes.number.isRequired,
    wireId: PropTypes.number,
  }

  static defaultProps = {
    wireId: null,
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      /* eslint-disable react/no-unused-state */
      partnership: new Partnership(),
      contacts: [],
      created_atSortDirection: SORT_DIRECTION.unsorted,
      nameSortDirection: SORT_DIRECTION.unsorted,
      nameFilter: '',
      emailSortDirection: SORT_DIRECTION.unsorted,
      emailFilter: '',
      phoneSortDirection: SORT_DIRECTION.unsorted,
      phoneFilter: '',
      investor_typeSortDirection: SORT_DIRECTION.unsorted,
      investor_typeFilter: '',
      sent_atSortDirection: SORT_DIRECTION.unsorted,
      wireIdFilter: props.wireId,
      filterOutArchivedContact: true,
      /* eslint-enable react/no-unused-state */
    };
  }

  componentDidMount() {
    postal.subscribe({
      channel: 'lwadmin.partnership.commands',
      topic: 'edit-partnership',
      callback: () => this.editPartnership(),
    });
    this.refresh();
  }

  editPartnership() {
    this.editPartnershipModal.present(this.state.partnership.id);
  }

  refresh() {
    this.loading.start();
    getById(this.props.partnershipId)
      .then(result => this.setState(oldState => mutate(oldState, state => {
        state.partnership = new Partnership(result.data);
        state.contacts = filterAndSortContacts(state.partnership.contacts, state);
      })))
      .catch(error => defaultOnErrorHandler(error, 'Error loading partnership'))
      .finally(() => this.loading.stop());
  }

  sortCreatedAt(direction) {
    this.setState(oldState => mutate(oldState, state => {
      state.created_atSortDirection = direction;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  sortName(direction) {
    this.setState(oldState => mutate(oldState, state => {
      state.nameSortDirection = direction;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  filterName(filter) {
    this.setState(oldState => mutate(oldState, state => {
      state.nameFilter = filter;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  sortEmail(direction) {
    this.setState(oldState => mutate(oldState, state => {
      state.emailSortDirection = direction;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  filterEmail(filter) {
    this.setState(oldState => mutate(oldState, state => {
      state.emailFilter = filter;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  sortPhone(direction) {
    this.setState(oldState => mutate(oldState, state => {
      state.phoneSortDirection = direction;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  filterPhone(filter) {
    this.setState(oldState => mutate(oldState, state => {
      state.phoneFilter = filter;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  sortInvestorType(direction) {
    this.setState(oldState => mutate(oldState, state => {
      state.investor_typeSortDirection = direction;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  filterInvestorType(filter) {
    this.setState(oldState => mutate(oldState, state => {
      state.investor_typeFilter = filter;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  sortSentAt(direction) {
    this.setState(oldState => mutate(oldState, state => {
      state.sent_atSortDirection = direction;
      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  viewContact(e, contact) {
    e.preventDefault();

    this.viewContactModal.present(this.state.partnership.id, contact.id);
  }

  toggleContactArchiving(evt, contact) {
    evt.preventDefault();

    toggleArchivingStatus(contact.id)
      .then(() => this.setState(oldState => mutate(oldState, state => {
        const c = _.find(state.partnership.contacts, { id: contact.id });
        c.archived = !c.archived;
        state.contacts = filterAndSortContacts(state.partnership.contacts, state);
      })))
      .catch(error => defaultOnErrorHandler(error, 'Error toggling contact archiving status'))
      .finally(() => this.loading.stop());
  }

  toggleShowHideArchivedContacts() {
    this.setState(oldState => mutate(oldState, state => {
      state.filterOutArchivedContact = !state.filterOutArchivedContact;

      state.contacts = filterAndSortContacts(state.partnership.contacts, state);
    }));
  }

  renderContactsTable() {
    return (
      <table className="table table-hover">
        <thead>
          <tr>
            <SortableFilterableTableHeader
              onSort={direction => this.sortCreatedAt(direction)}
            >
              Created at
            </SortableFilterableTableHeader>
            <SortableFilterableTableHeader
              onSort={direction => this.sortName(direction)}
              onFilter={filter => this.filterName(filter)}
            >
              Name
            </SortableFilterableTableHeader>
            <SortableFilterableTableHeader
              onSort={direction => this.sortEmail(direction)}
              onFilter={filter => this.filterEmail(filter)}
            >
              Email
            </SortableFilterableTableHeader>
            <SortableFilterableTableHeader
              onSort={direction => this.sortPhone(direction)}
              onFilter={filter => this.filterPhone(filter)}
            >
              Phone
            </SortableFilterableTableHeader>
            <SortableFilterableTableHeader
              onSort={direction => this.sortInvestorType(direction)}
              onFilter={filter => this.filterInvestorType(filter)}
            >
              Investor type
            </SortableFilterableTableHeader>
            <SortableFilterableTableHeader onSort={direction => this.sortSentAt(direction)}>
              Sent at
            </SortableFilterableTableHeader>
            <th>Tools</th>
          </tr>
        </thead>
        <tbody>
          {this.state.contacts.map(contact => (
            <tr key={contact.id}>
              <td>{contact.created_at.format('LLLL')}</td>
              <td>{contact.name}</td>
              <td>{contact.email}</td>
              <td>{contact.phone}</td>
              <td>{contact.investor_type}</td>
              <td>
                {!!contact.sent_at && contact.sent_at.format('LLLL')}
                {!contact.sent_at && 'Not sent'}
              </td>
              <td className="table-tools">
                <ul>
                  <li><a href="#" onClick={e => this.viewContact(e, contact)}>View</a></li>
                  <li><a href="#" onClick={e => this.toggleContactArchiving(e, contact)}>{contact.archived ? 'Restore' : 'Archive'}</a></li>
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  render() {
    const CheckboxLabel = styled.label`
      float: right;
      margin-top: -30px;
      margin-right: 20px;

      input[type=checkbox] {
        top: -2px;
      }
    `;

    return (
      <ErrorBoundary>
        <div className="lwadmin-partnership-admin">
          <Loading ref={c => (this.loading = c)}>
            <h1>
              <a href="/lwadmin/partnerships">Partnerships</a>
              {' | '}
              <a href={`/lwadmin/partnership/${this.state.partnership.id}/wires`}>
                {this.state.partnership.name}
              </a>
              {' | Enquiries'}
            </h1>

            {!this.state.partnership.active && (
              <p className="alert alert-warning">This partnership has been deactivated</p>
            )}

            <p>
              <strong>Partnership type:</strong>
              {` ${this.state.partnership.partnership_type}`}
              <br />
              <strong>
                <span>
                  {'Contacts are'}
                  {
                    this.state.partnership.send_automatically
                      ? ' '
                      : (
                        <React.Fragment>
                          {' '}
                          <em>not</em>
                          {' '}
                        </React.Fragment>
                      )
                  }
                  {'sent automatically'}
                </span>
              </strong>
              <br />
              <strong>Contact:</strong>
              {' '}
              <a href={`mailto:${this.state.partnership.contact_email}`}>
                {this.state.partnership.contact_email}
              </a>
            </p>

            <section data-role="tab-set">
              <nav>
                <ul>
                  <li>
                    <a href="#contacts" className="active">Contacts/leads</a>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={!this.state.filterOutArchivedContact}
                        onChange={() => this.toggleShowHideArchivedContacts()}
                      />
                      Show archived
                    </CheckboxLabel>
                  </li>
                </ul>
              </nav>
              <section data-role="tab-panel" id="contacts" className="active">
                {this.renderContactsTable()}
              </section>
            </section>
          </Loading>
          <EditPartnershipModal
            ref={c => (this.editPartnershipModal = c)}
            partnershipDidSave={() => this.refresh()}
          />
          <ViewContactModal
            ref={c => (this.viewContactModal = c)}
            contactWasSent={() => this.refresh()}
          />
        </div>
      </ErrorBoundary>
    );
  }
}
